import { useTranslation } from "../../state/i18n";
import { type Component, type JSX, Show } from "solid-js";
import { Dialog } from "@ark-ui/solid";
import { DynamicValueIconTile } from "../icon/IconTile";
import Text from "../text/Text";
import { IconButton } from "../button/Button";
import { isNil, omit } from "lodash-es";
import type { IconName } from "../icon/Icon";
import { Icon } from "../icon/Icon";
import type { Category } from "../../types";
import { condition } from "../../utils/solidUtils";
import { getIconCategoryColor } from "../../utils/iconColorUtil";
import { useCurrentModal } from "./ModalManager";

type DetailModalProps = {
  title: string;
  iconTile?: {
    icon: IconName;
    category: Category;
  };
  content: () => JSX.Element;
  footer?: JSX.Element;
  description?: string;
  fullHeight?: boolean;
};
const DetailModal: Component<DetailModalProps> = (props) => {
  const { t } = useTranslation();
  const { position, close, canClose } = useCurrentModal();

  return (
    <div
      class="flex max-h-160 w-146 flex-col shadow-elevation-light-5"
      classList={{ "h-160": props.fullHeight === true }}
    >
      <div class="flex flex-col gap-0.5 p-6">
        <Dialog.Title class="flex">
          <Show when={position() > 0}>
            <BackButton onClick={() => close()} />
          </Show>
          <div class="mr-10 flex gap-2 overflow-hidden" classList={{ "grow justify-center": position() > 0 }}>
            <Show when={condition(props.iconTile, (iconTile) => !isNil(iconTile))}>
              {(iconTile) => (
                <DynamicValueIconTile
                  icon={iconTile().icon}
                  iconClassList={{ [getIconCategoryColor(iconTile().category)]: true }}
                  size="md"
                />
              )}
            </Show>
            <Text class="text-header-lg text-primary" text={props.title} truncate />
          </div>
        </Dialog.Title>

        <Show when={!isNil(props.description)}>
          <p class="text-medium-regular text-secondary">{props.description}</p>
        </Show>

        <div class="absolute top-3 right-3">
          <Dialog.CloseTrigger
            asChild={(parentProps) => (
              <IconButton
                {...omit(parentProps(), "children")}
                icon="x"
                aria-label={t(($) => $.CLOSE_ARIA)}
                size="large"
                variant="tertiary"
                disabled={!canClose()}
              />
            )}
          />
        </div>
      </div>
      <div class="grow overflow-y-auto px-6 pb-6">{props.content()}</div>
      <Show when={!isNil(props.footer)}>
        <div class="border-t border-subtle p-6">{props.footer}</div>
      </Show>
    </div>
  );
};

type BackButtonProps = {
  onClick: () => void;
};
const BackButton: Component<BackButtonProps> = (props) => {
  const { t } = useTranslation();

  return (
    <button
      class="flex max-w-45 items-center gap-1 pr-1 text-base-medium text-button-text-tertiary-default hover:text-button-text-tertiary-default-hover focus-visible:outline-default active:text-button-text-tertiary-default-pressed"
      type="button"
      onClick={() => props.onClick()}
    >
      <Icon name="chevronLeft" size="md" type="solid" class="shrink-0" />
      <Text text={t(($) => $.DETAIL_MODAL_BACK)} truncate />
    </button>
  );
};

export default DetailModal;
